import api from '@/utils/api';
import review from '@/utils/timesheetReviewGrid';

const queryKeys = {sortKey: 'rsort', sortDirection: 'rdir', search: 'rq', object: 'robject', author: 'rauthor', category: 'rcategory', status: 'rstatus', issue: 'rissue'};

export default {
    data: () => ({reviewGrid: null, reviewGridGeneratedAt: null, reviewGridLoading: false, reviewGridError: '', reviewGridRequest: 0,
        reviewViewState: review.viewState(), reviewFocusMessage: ''}),
    created() {
        this._reviewPositions = new Map();
        this._reviewRefreshes = new Map();
        this._reviewGridComparison = null;
        this._reviewStale = {grid: true, readiness: true};
    },
    beforeDestroy() { if (this.reviewMode) this.saveReviewView(); this.reviewGridRequest += 1; this._reviewDisposed = true; },
    computed: {
        reviewFocusedLineId() {
            if (this.reviewMode || this.periodOrigin !== 'review' || !this.sheet) return null;
            const id = Number(this.$route.query.review_line);
            return (this.sheet.lines || []).some(line => Number(line.id) === id) ? id : null;
        },
        reviewFocusedDate() {
            if (!this.reviewFocusedLineId) return '';
            const date = this.$route.query.review_date;
            return this.monthDays.some(day => day.date === date) ? date : '';
        }
    },
    methods: {
        clearReviewAccess() {
            this.reviewRequestId += 1;
            this.reviewGridRequest += 1;
            this.periodSummary = null;
            this.reviewGrid = null;
            this.reviewGridGeneratedAt = null;
            this.reviewSheets = [];
            this.reviewLoading = false;
            this.reviewGridLoading = false;
            this._reviewAccessEpoch = (this._reviewAccessEpoch || 0) + 1;
            this._reviewGridComparison = null;
            this.bootstrapRefreshPending = true;
        },
        reviewSourceCellClass(line, date) {
            return {'review-source-day': date === this.reviewFocusedDate && Boolean(date),
                'review-source-cell': Number(line.id) === this.reviewFocusedLineId && date === this.reviewFocusedDate && Boolean(date)};
        },
        saveReviewView(position) {
            if (position && position.month && position.month !== this.periodMonth) return;
            const table = this.$refs.reviewGrid;
            const current = position || table && table.currentPosition() || this._reviewPositions.get(`${this.periodMonth}:${this.reviewViewState.view}`) || {};
            const coordinates = {...current, ...(typeof window !== 'undefined' ? {pageScrollTop: window.scrollY} : {})};
            this._reviewPositions.set(`${this.periodMonth}:${this.reviewViewState.view}`,  coordinates);
            // Scroll events must not replace reviewViewState and invalidate the
            // workspace render. The table emits only after scrolling settles.
            this.rememberPeriod(`review-grid:${this.periodMonth}${this.reviewViewState.view === 'days' ? ':days' : ''}`, {...this.reviewViewState, ...coordinates, month: this.periodMonth});
        },
        async restoreReviewScroll() {
            await this.$nextTick();
            const position = this._reviewPositions.get(`${this.periodMonth}:${this.reviewViewState.view}`) || this.reviewViewState;
            if (typeof window !== 'undefined') window.scrollTo(0, position.pageScrollTop || 0);
        },
        initialReviewView(month) {
            const saved = this.savedPeriod(`review-grid:${month}${this.$route.query.review_view === 'days' ? ':days' : ''}`);
            return review.viewState(saved && saved.view ? saved : {view: this.bootstrapData.permissions.can_review ? 'grid' : 'readiness'});
        },
        reviewQueryForMonth(month) {
            const state = month === this.periodMonth && (this.reviewMode || this.periodOrigin === 'review') ? this.reviewViewState : this.initialReviewView(month);
            const query = {review_view: state.view, record_kind: state.kind};
            if (state.includeDrafts) query.include_drafts = '1';
            Object.keys(queryKeys).forEach(key => { if (state.filters[key]) query[queryKeys[key]] = state.filters[key]; });
            if (state.filters.onlyIssues) query.rissues = '1';
            return query;
        },
        restoreReviewView(route) {
            const saved = this.initialReviewView(this.periodMonth);
            const query = route.query || {};
            if (query.review_view || query.record_kind || query.include_drafts) {
                const filters = review.defaults();
                Object.keys(queryKeys).forEach(key => { if (typeof query[queryKeys[key]] === 'string') filters[key] = query[queryKeys[key]]; });
                filters.onlyIssues = query.rissues === '1';
                this.reviewViewState = review.viewState({...saved, view: query.review_view, kind: query.record_kind, includeDrafts: query.include_drafts === '1', filters});
            } else this.reviewViewState = saved;
            this._reviewPositions.set(`${this.periodMonth}:${this.reviewViewState.view}`,  {scrollTop: this.reviewViewState.scrollTop,
                scrollLeft: this.reviewViewState.scrollLeft, pageScrollTop: this.reviewViewState.pageScrollTop});
        },
        async changeReviewView(view) {
            if (this.periodNavigationLocked) return;
            this.saveReviewView();
            const target = this.periodListRoute();
            target.query.review_view = view;
            target.query.record_kind = 'official';
            await this.pushPeriodRoute(target);
        },
        async changeReviewFilters(filters) {
            this.reviewViewState = review.viewState({...this.reviewViewState, filters});
            this.saveReviewView();
            await this.syncPeriodRoute(this.periodListRoute());
        },
        async resetReviewFilters(filters) {
            if (this.periodNavigationLocked) return;
            this.reviewViewState = review.viewState({...this.reviewViewState, filters});
            const target = this.periodListRoute();
            delete target.query.include_drafts;
            await this.pushPeriodRoute(target);
        },
        async changeReviewDrafts(enabled) {
            if (this.periodNavigationLocked) return;
            this.saveReviewView();
            const target = this.periodListRoute();
            if (enabled) target.query.include_drafts = '1';
            else delete target.query.include_drafts;
            await this.pushPeriodRoute(target);
        },
        async loadReviewGrid({background = false} = {}) {
            const request = ++this.reviewGridRequest;
            const month = this.periodMonth;
            const kind = this.reviewViewState.kind;
            const includeDrafts = this.reviewViewState.includeDrafts;
            if (!background) this.reviewGrid = null;
            this.reviewGridError = '';
            if (!background) this.reviewGridLoading = true;
            try {
                const response = await api.get('/api/timesheets/periods/review-grid', {params: {period_start: `${month}-01`, record_kind: kind, include_drafts: includeDrafts ? '1' : '0'}});
                if (request !== this.reviewGridRequest || !this.reviewMode || month !== this.periodMonth || kind !== this.reviewViewState.kind || includeDrafts !== this.reviewViewState.includeDrafts) return false;
                if (!response.data.period || response.data.period.starts_on !== `${month}-01` || response.data.record_kind !== kind) throw new Error('Period mismatch');
                if (Boolean(response.data.include_drafts) !== includeDrafts) throw new Error('Draft context mismatch');
                const comparison = review.comparable(response.data);
                if (!this.reviewGrid || comparison !== this._reviewGridComparison) {
                    this._reviewGridComparison = comparison;
                    this.reviewGrid = review.snapshot(response.data);
                }
                this.reviewGridGeneratedAt = response.data.generated_at;
                this._reviewStale.grid = false;
                return true;
            } catch (error) {
                if (request === this.reviewGridRequest) {
                    this.reviewGrid = null;
                    this.reviewGridError = this.responseMessage(error, 'Не удалось загрузить свод поданных табелей.');
                    if (error && error.response && error.response.status === 403) this.clearReviewAccess();
                }
                return false;
            } finally { if (request === this.reviewGridRequest) this.reviewGridLoading = false; }
        },
        activeReviewView() { return this.reviewViewState.view === 'days' ? 'grid' : this.reviewViewState.view; },
        refreshActiveReview() {
            this._reviewStale.grid = true;
            this._reviewStale.readiness = true;
            const view = this.activeReviewView();
            return this.loadActiveReview(view, true);
        },
        loadActiveReview(view = this.activeReviewView(), repeat = false) {
            const month = this.periodMonth, kind = this.reviewViewState.kind, drafts = this.reviewViewState.includeDrafts;
            const epoch = this._reviewAccessEpoch || 0;
            const generation = this._reviewViewEpoch || 0;
            const key = `${epoch}:${generation}:${month}:${view}:${kind}:${drafts}`;
            const existing = this._reviewRefreshes.get(key);
            if (existing) { existing.repeat = existing.repeat || repeat; return existing.promise; }
            const job = {repeat: false, promise: null};
            const current = () => !this._reviewDisposed && this.reviewMode && month === this.periodMonth
                && epoch === (this._reviewAccessEpoch || 0) && generation === (this._reviewViewEpoch || 0) && view === this.activeReviewView()
                && (view !== 'grid' || kind === this.reviewViewState.kind && drafts === this.reviewViewState.includeDrafts);
            const run = async () => {
                let success = false;
                do {
                    job.repeat = false;
                    if (!current()) return false;
                    success = view === 'grid' ? await this.loadReviewGrid({background: Boolean(this.reviewGrid)}) : await this.loadReview();
                    if (success && current()) this._reviewStale[view] = false;
                } while (job.repeat && current());
                return success;
            };
            this._reviewRefreshes.set(key, job);
            job.promise = run().finally(() => { if (this._reviewRefreshes.get(key) === job) this._reviewRefreshes.delete(key); });
            return job.promise;
        },
        reviewGridSourceRoute({source, row, date}) {
            return {name: 'timesheet', params: {timesheetId: String(source.timesheet_id)}, query: {
                from: 'review', month: this.periodMonth, ...this.reviewQueryForMonth(this.periodMonth),
                ...(source.revision_id ? {review_revision: String(source.revision_id)} : {}), review_line: String(row.line_id),
                ...(date ? {review_date: date} : {})
            }};
        },
        reviewGridSourceLink(payload) {
            return this.$router.resolve(this.reviewGridSourceRoute(payload)).href;
        },
        async focusReviewSource() {
            if (!this.sheet || this.periodOrigin !== 'review') return;
            const sheetId = this.sheet.timesheet.id;
            const routePath = this.$route.fullPath;
            const revision = this.sheet.review_revision;
            this.reviewFocusMessage = revision && revision.unavailable ? 'Поданная версия из свода больше недоступна. Показаны текущие данные табеля.'
                : revision && revision.changed ? `Данные табеля отличаются от версии ${revision.revision_number}, показанной в своде. Здесь открыты текущие данные.` : '';
            const lineId = Number(this.$route.query.review_line);
            if (!lineId) return;
            const line = this.sheet.lines.find(item => Number(item.id) === lineId);
            if (!line) { this.reviewFocusMessage += ' Строка из свода больше не существует в текущем табеле.'; return; }
            this.revealEmployeeSearchLine(lineId);
            const date = this.$route.query.review_date;
            const validDate = typeof date === 'string' && this.monthDays.some(day => day.date === date);
            if (validDate) {
                this.selectedDate = date;
                const week = this.monthWeeks.find(item => item.days.some(day => day.date === date));
                if (week) { this.$set(this.collapsedWeekKeys, week.key, false); this.mobileWeekKey = week.key; }
                this.rebuildDrafts();
            }
            await this.$nextTick();
            if (!this.sheet || this.sheet.timesheet.id !== sheetId || this.$route.fullPath !== routePath) return;
            if (this._reviewFocusedElement) this._reviewFocusedElement.classList.remove('review-source-focus');
            if (this.$refs.monthRows) await this.$refs.monthRows.reveal(lineId);
            if (!this.sheet || this.sheet.timesheet.id !== sheetId || this.$route.fullPath !== routePath) return;
            const row = Array.from(this.$el.querySelectorAll(`[data-review-line-id="${lineId}"]`)).find(item => item.getClientRects().length);
            const cell = validDate && row && Array.from(row.querySelectorAll(`[data-line-id="${lineId}"][data-work-date="${date}"]`))
                .find(item => item.getClientRects().length);
            const element = cell || row;
            if (element) {
                element.scrollIntoView({behavior: 'auto', block: 'center', inline: validDate ? 'center' : 'nearest'});
                element.classList.add('review-source-focus');
                this._reviewFocusedElement = element;
            }
        }
    }
};
